import { inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { CallWaiterService, HappyHoursService, RestaurantTableService } from '@core';
import { PageService } from '@app/services/page';
import { BackNavigationService } from '@app/services/back-navigation';
import { BuilderAction, BuilderDataSource } from '@app/schemas/builder';
import { TextTranslatableSchema } from '@app/schemas/text';
import { getBuilderButtonState, getBuilderDataText, SERVICE_REQUEST_TYPES } from './builder-runtime';
import { builderRequestDataType, isBuilderClockDataSource, type BuilderDataContext } from './data';
import { map, timer } from 'rxjs';
import type { BuilderWeatherSnapshot } from './weather';

@Injectable()
export class BuilderRuntimeService {
  private readonly callWaiter = inject(CallWaiterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly backNavigation = inject(BackNavigationService);

  private readonly pages = toSignal(inject(PageService).config$, { initialValue: [] });
  private readonly table = toSignal(inject(RestaurantTableService).table$);
  private readonly happyHours = toSignal(inject(HappyHoursService).happyHours$, { initialValue: [] });
  // This service is page-scoped; toSignal releases the single clock when the page is destroyed.
  private readonly now = toSignal(timer(0, 1_000).pipe(map(() => Date.now())), { initialValue: Date.now() });
  private readonly lastCallAt = {
    main: toSignal(this.callWaiter.lastCallAtByType$.main, { initialValue: null }),
    secondary: toSignal(this.callWaiter.lastCallAtByType$.secondary, { initialValue: null }),
    tertiary: toSignal(this.callWaiter.lastCallAtByType$.tertiary, { initialValue: null }),
  };
  private readonly status = toSignal(this.callWaiter.status$, {
    initialValue: { main: false, secondary: false, tertiary: false },
  });
  private readonly available = toSignal(this.callWaiter.with$, {
    initialValue: { main: false, secondary: false, tertiary: false },
  });

  public buttonState(action: BuilderAction | undefined) {
    return getBuilderButtonState(action, this.status(), this.available(), this.pages().map((page) => page.id));
  }

  public dataText(source: BuilderDataSource, fallback: TextTranslatableSchema | undefined, language: string,
    weather?: BuilderWeatherSnapshot): string {
    const context: BuilderDataContext = { weather };
    // Static text does not read the clock signal and therefore stays independent of each tick.
    if (isBuilderClockDataSource(source)) context.nowMs = this.now();
    if (source.type === 'happyHour.next.start' || source.type === 'happyHour.current.end') context.happyHours = this.happyHours();
    if (source.type.endsWith('.elapsed')) {
      const type = builderRequestDataType(source);
      if (type) {
        context.status = this.status();
        context.lastCallAt = { [type]: this.lastCallAt[type]() };
      }
    }
    return getBuilderDataText(source, this.table(), fallback, language, context);
  }

  public trackingName(action: BuilderAction | undefined): string {
    if (action?.type === 'back') return 'Back';

    if (action?.type === 'service-request') {
      const type = SERVICE_REQUEST_TYPES[action.request];
      return this.callWaiter.getTrackingName(type, this.status()[type]);
    }

    if (action?.type === 'page') {
      const page = this.pages().find((candidate) => candidate.id === action.pageId);
      return `Open ${ page?.name.translations[page.name.fallback] || action.pageId }`;
    }

    return 'Builder button';
  }

  public execute(action: BuilderAction | undefined, pageId: string): void {
    const state = this.buttonState(action);
    if (!action || !state.visible || state.disabled) {
      return;
    }

    if (action.type === 'back') {
      this.backNavigation.back(this.route.snapshot.queryParamMap.getAll('ref'));
      return;
    }

    if (action.type === 'page') {
      void this.router.navigate(['/', 'main', 'page', action.pageId], {
        queryParams: { ref: [...this.route.snapshot.queryParamMap.getAll('ref'), `page:${ pageId }`] },
      });
      return;
    }

    const nextStatus = action.toggle === false || !state.active;
    switch (action.request) {
      case 'call-waiter':
        this.callWaiter.setMain(nextStatus);
        break;
      case 'request-bill':
        this.callWaiter.setSecondary(nextStatus);
        break;
      case 'another-round':
        this.callWaiter.setTertiary(nextStatus);
        break;
    }
  }
}
